import React, { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react'
import { TableSectionWell } from '../../components/section-well'
import { useOpenInDomainContext } from '../../data/use-open-in-domain-context'
import { DashboardDomain } from '../../types'
import { TopNotificationReportRow } from '../../data/contracts'
import { NotificationRankingChoice, RankingTable } from '../../data/use-org-dashboard-rankings'
import { DateWindow } from '../../math/windows'
import { DashboardTemplate } from '../../template'
import { TopNotificationsTable } from './top-notifications-table'

type Props = {
    template: DashboardTemplate
    rankings: { notifications: RankingTable<TopNotificationReportRow> }
    domains: DashboardDomain[]
    range: DateWindow
    mixedCurrency: boolean
    notificationRanking: NotificationRankingChoice
    onNotificationRankingChange: (ranking: NotificationRankingChoice) => void
    onRetry: () => void
    /** The definitions leave out the measure Top Notifications ranks by, so the table is not drawn at all. */
    topNotificationsWithheld?: boolean
}

/**
 * Top Notifications across the organization. It observes the user's switchable domains, which decide whether a
 * notification title is a link.
 */
export const ContentSection: React.FC<Props> = observer(
    ({
        template,
        rankings,
        domains,
        range,
        mixedCurrency,
        notificationRanking,
        onNotificationRankingChange,
        onRetry,
        topNotificationsWithheld = false,
    }) => {
        const { canOpenDomain, openInDomain } = useOpenInDomainContext()
        const domainsById = useMemo(() => new Map(domains.map((domain) => [domain.id, domain])), [domains])
        const currencyCodeFor = useCallback(
            (domainId: number) => domainsById.get(domainId)?.currencyCode,
            [domainsById],
        )

        return (
            <TableSectionWell sectionKey="content">
                <div className="org-dashboard-content">
                    {!topNotificationsWithheld && (
                        <TopNotificationsTable
                            template={template}
                            mixedCurrency={mixedCurrency}
                            table={rankings.notifications}
                            range={range}
                            ranking={notificationRanking}
                            onRankingChange={onNotificationRankingChange}
                            currencyCodeFor={currencyCodeFor}
                            canOpenDomain={canOpenDomain}
                            openInDomain={openInDomain}
                            onRetry={onRetry}
                        />
                    )}
                </div>
            </TableSectionWell>
        )
    },
)
